import React, { useState } from 'react';

import BoardMeeting from '../../media/images/modules/board-meeting.jpg';
import FormContractor from '../../media/images/modules/form-contractor.jpg';
import FormGuest from '../../media/images/modules/form-guest.jpg';
import FormKey from '../../media/images/modules/form-key.jpg';
import FormPet from '../../media/images/modules/form-pet.jpg';
import FormRenovation from '../../media/images/modules/form-renovation.jpg';
import Garage from '../../media/images/modules/parking-garage.jpg';
import { ScreenHead } from '../../ui/screen-parts.js';
import { Chip, Kpis, Panel, Segments, Empty, useFlash } from '../../ui/kit.js';
import { useBuilding } from '../../state/building.js';

const FILTERS = ['Pending', 'Approved', 'Declined', 'All'];
const STATUS_TONE = { 'Pending': 'warn', 'Approved': 'ok', 'Declined': 'alert' };

/* A photo for each kind of request, shown at the top of its panel. */
const KIND_PHOTO = {
    'Renovation request': FormRenovation,
    'Contractor access': FormContractor,
    'Key/fob replacement': FormKey,
    'Pet addendum': FormPet,
    'Short-term guest authorization': FormGuest,
    'Parking request': Garage,
    'Board spend': BoardMeeting
};

const dollars = amount => Number(String(amount || '').replace(/[^0-9.]/g, '')) || 0;

/* Board and Management Approvals, with Digital Forms review: requests from
   residents and spend from management, decided here and seen everywhere. */
function ApprovalsScreen() {
    const { approvals, moves, patch } = useBuilding();
    const [filter, setFilter] = useState('Pending');
    const [flash, notify] = useFlash();

    const decide = (item, status) => {
        patch('approvals', item.id, { status });
        notify(`${item.title} ${status.toLowerCase()}. ${item.from} has been notified.`);
    };

    const decideMove = (move, status) => {
        patch('moves', move.id, { status });
        notify(`${move.kind} for Unit ${move.unit} ${status === 'Confirmed' ? 'approved. Elevator and dock are reserved' : 'declined'}.`);
    };

    const shown = approvals.filter(a => filter === 'All' || a.status === filter);
    const approved = approvals.filter(a => a.status === 'Approved');
    const pendingMoves = moves.filter(m => m.status === 'Pending');
    const spend = approved.reduce((sum, a) => sum + dollars(a.amount), 0);

    return (
        <div className="screen-content">
            <ScreenHead eyebrow="Approvals" title="Approvals and forms review" />

            <div className="banner">
                <img src={BoardMeeting} alt="" />
                <p className="banner__caption">Resident requests and board spend, decided in one place.</p>
            </div>

            <Kpis items={[
                { label: 'Pending', value: approvals.filter(a => a.status === 'Pending').length + pendingMoves.length, tone: 'warn' },
                { label: 'Approved this month', value: approved.length, tone: 'ok' },
                { label: 'Spend approved', value: `$${spend.toLocaleString()}`, tone: 'info', note: 'Board spend, this month' }
            ]} />

            {flash}

            <Segments label="Status" options={FILTERS} value={filter} onChange={setFilter} />

            <div className="cards cards--wide section-gap">
                {shown.length === 0 && <Empty>No {filter.toLowerCase()} requests.</Empty>}
                {shown.map((item, i) => (
                    <Panel key={item.id} index={i} layout className={item.status === 'Pending' ? 'panel--accent' : ''}>
                        {KIND_PHOTO[item.kind] && <img className="panel__media" src={KIND_PHOTO[item.kind]} alt={item.kind} loading="lazy" />}
                        <div className="row__end" style={{ justifyContent: 'space-between' }}>
                            <Chip tone="muted">{item.kind}</Chip>
                            <Chip tone={STATUS_TONE[item.status]}>{item.status}</Chip>
                        </div>
                        <h4>{item.title}</h4>
                        <p className="row__meta">From {item.from}{item.amount ? ` · ${item.amount}` : ''}</p>
                        <p>{item.detail}</p>
                        {item.status === 'Pending' && (
                            <div className="row__end" style={{ justifyContent: 'flex-start' }}>
                                <button type="button" className="btn btn-primary btn-sm" onClick={() => decide(item, 'Approved')}>Approve</button>
                                <button type="button" className="btn btn-outline-light btn-sm" onClick={() => decide(item, 'Declined')}>Decline</button>
                            </div>
                        )}
                    </Panel>
                ))}
            </div>

            <div className="section-gap stack">
                <h4>Move requests awaiting approval</h4>
                {pendingMoves.length === 0 ? <Empty>No moves waiting.</Empty> : (
                    <ul className="rows">
                        {pendingMoves.map(move => (
                            <li className="row" key={move.id}>
                                <div className="row__main">
                                    <span className="row__title">{move.kind}, Unit {move.unit}</span>
                                    <span className="row__meta">{move.date} · {move.slot} · service elevator and dock</span>
                                </div>
                                <div className="row__end">
                                    <Chip tone="warn">{move.status}</Chip>
                                    <button type="button" className="btn btn-primary btn-sm" onClick={() => decideMove(move, 'Confirmed')}>Approve</button>
                                    <button type="button" className="btn btn-outline-light btn-sm" onClick={() => decideMove(move, 'Declined')}>Decline</button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

export default ApprovalsScreen;
